import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import merchantProvider from "src/providers/merchant-provider";
import type { ListPaymentParams, PaymentParams } from "src/types";
import { sleep } from "src/utils";

const DEFAULT_PAGE_SIZE = 20;

const paymentsQueries = {
	listPayments: (params?: Omit<ListPaymentParams, "limit" | "offset">) => {
		const { merchantId } = useActiveMerchantId();

		return useQuery({
			queryKey: [
				"listPayments",
				merchantId ?? "no-merchant",
				params?.search,
				params?.status,
			],
			queryFn: async () => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}

				// Fetch all payments without pagination for client-side filtering
				// This matches the pattern used for orders page
				return merchantProvider.listPayments(merchantId, {
					limit: 1000,
					offset: 0,
					search: params?.search || "",
					status: params?.status,
				});
			},
			staleTime: 30000,
			enabled: Boolean(merchantId),
			retry: 2,
		});
	},

	listPaymentsPaginated: (params: ListPaymentParams) => {
		const { merchantId } = useActiveMerchantId();

		return useQuery({
			queryKey: [
				"listPaymentsPaginated",
				merchantId ?? "no-merchant",
				params.limit ?? DEFAULT_PAGE_SIZE,
				params.offset ?? 0,
				params.search,
				params.status,
			],
			queryFn: async () => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}

				return merchantProvider.listPayments(merchantId, {
					limit: params.limit ?? DEFAULT_PAGE_SIZE,
					offset: params.offset ?? 0,
					search: params.search || "",
					status: params.status,
				});
			},
			staleTime: 30000,
			enabled: Boolean(merchantId),
			retry: 2,
		});
	},

	createPayment: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (params: PaymentParams) => {
				if (!merchantId) {
					throw new Error("Missing merchantId");
				}
				return merchantProvider.createPayment(merchantId, params);
			},
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({
					queryKey: ["listPayments"],
				});
				queryClient.invalidateQueries({
					queryKey: ["listPaymentsPaginated"],
				});
			},
		});
	},

	getPayment: () => {
		const { merchantId } = useActiveMerchantId();

		return useMutation({
			mutationFn: (paymentId: string) => {
				if (!merchantId) {
					throw new Error("Missing merchantId");
				}
				return merchantProvider.getPayment(merchantId, paymentId);
			},
		});
	},
};

export default paymentsQueries;
